import {
    resolveAccountPageRoot,
    resolveChangeEmailForm,
    resolveChangePasswordForm,
    resolveDeleteForm,
} from './dom.js';
import { initValidatedForm } from '../../components/form-validation.js';
import { initAccountExport } from './export/controller.js';
import { initAccountImport } from './import/controller.js';

const pageControllers = new WeakMap();

export function initAccountPage(documentRef = globalThis.document, fetchImpl = globalThis.fetch, timerApi = globalThis) {
    if (typeof fetchImpl !== 'function'
        || documentRef === undefined
        || documentRef === null
        || typeof documentRef.querySelector !== 'function') {
        return;
    }

    const page = resolveAccountPageRoot(documentRef);
    if (page === null) {
        return;
    }

    const existingController = pageControllers.get(page);
    if (existingController !== undefined) {
        return existingController;
    }

    const validationController = initSecurityFormValidation(page);
    const deleteGateController = initDeleteSubmitState(page);
    const errorScrollController = initErrorScroll(page);
    const emailDraftController = initEmailDraft(page, documentRef.defaultView?.sessionStorage);
    const exportJumpController = initExportJump(page, timerApi);
    const exportController = initAccountExport(documentRef, fetchImpl, page, timerApi);
    const importController = initAccountImport(documentRef, fetchImpl, page);

    if (validationController === undefined
        && deleteGateController === undefined
        && errorScrollController === undefined
        && emailDraftController === undefined
        && exportJumpController === undefined
        && exportController === undefined
        && importController === undefined) {
        pageControllers.delete(page);
        return;
    }

    const controller = {
        destroy() {
            validationController?.destroy();
            deleteGateController?.destroy();
            errorScrollController?.destroy();
            emailDraftController?.destroy();
            exportJumpController?.destroy();
            exportController?.destroy();
            importController?.destroy();
            pageControllers.delete(page);
        },
    };

    pageControllers.set(page, controller);

    return controller;
}

function initSecurityFormValidation(page) {
    const controllers = [
        initChangeEmailValidation(page),
        initChangePasswordValidation(page),
    ].filter((controller) => controller !== undefined);

    if (controllers.length === 0) {
        return undefined;
    }

    return {
        destroy() {
            for (const controller of controllers) {
                controller.destroy();
            }
        },
    };
}

function initChangeEmailValidation(page) {
    const formParts = resolveChangeEmailForm(page);
    if (formParts === null) {
        return undefined;
    }

    const { form, passwordInput, emailInput, passwordField, emailField } = formParts;

    return initValidatedForm({
        form,
        fields: [
            {
                input: passwordInput,
                errorContainer: passwordField,
                errorElement: passwordField.querySelector('.ui-form-error'),
                errorClassName: 'ui-form-error',
                messages: {
                    valueMissing: 'Password is required.',
                },
            },
            {
                input: emailInput,
                errorContainer: emailField,
                errorElement: emailField.querySelector('.ui-form-error'),
                errorClassName: 'ui-form-error',
                messages: {
                    valueMissing: 'Enter a valid email address.',
                    typeMismatch: 'Enter a valid email address.',
                },
            },
        ],
    });
}

function initChangePasswordValidation(page) {
    const formParts = resolveChangePasswordForm(page);
    if (formParts === null) {
        return undefined;
    }

    const {
        form,
        currentPasswordInput,
        newPasswordInput,
        confirmPasswordInput,
        currentPasswordField,
        newPasswordField,
        confirmPasswordField,
    } = formParts;

    return initValidatedForm({
        form,
        fields: [
            {
                input: currentPasswordInput,
                errorContainer: currentPasswordField,
                errorElement: currentPasswordField.querySelector('.ui-form-error'),
                errorClassName: 'ui-form-error',
                messages: {
                    valueMissing: 'Current password is required.',
                },
            },
            {
                input: newPasswordInput,
                errorContainer: newPasswordField,
                errorElement: newPasswordField.querySelector('.ui-form-error'),
                errorClassName: 'ui-form-error',
                messages: {
                    valueMissing: 'Password is required.',
                    tooShort: 'Password must be at least 8 characters.',
                },
            },
            {
                input: confirmPasswordInput,
                errorContainer: confirmPasswordField,
                errorElement: confirmPasswordField.querySelector('.ui-form-error'),
                errorClassName: 'ui-form-error',
                messages: {
                    valueMissing: 'Confirm your new password.',
                },
                getCustomMessage() {
                    if (confirmPasswordInput.value === '') {
                        return '';
                    }

                    return newPasswordInput.value === confirmPasswordInput.value
                        ? ''
                        : 'Passwords do not match.';
                },
            },
        ],
    });
}

function initDeleteSubmitState(page) {
    const formParts = resolveDeleteForm(page);
    if (formParts === null) {
        return undefined;
    }

    return initRequiredFieldsSubmitState(
        formParts.submitButton,
        [{ input: formParts.passwordInput }],
    );
}

function initRequiredFieldsSubmitState(submitButton, fieldConfigs) {
    const syncSubmitState = () => {
        submitButton.disabled = fieldConfigs.some(({ input, trim = false }) => {
            const value = trim ? input.value.trim() : input.value;

            return value === '';
        });
    };

    for (const { input } of fieldConfigs) {
        input.addEventListener('input', syncSubmitState);
    }

    syncSubmitState();

    return {
        destroy() {
            for (const { input } of fieldConfigs) {
                input.removeEventListener('input', syncSubmitState);
            }
        },
    };
}

function initErrorScroll(page) {
    const targetSelector = String(page.dataset.accountScrollTarget ?? '').trim();
    if (targetSelector === '') {
        return undefined;
    }

    const target = page.querySelector(targetSelector);
    if (!(target instanceof HTMLElement) || typeof target.scrollIntoView !== 'function') {
        return undefined;
    }

    // `scroll-margin-top` on the row keeps it below the sticky header; focus then lands on the first
    // field the server refused (or the section message), without a second jump.
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const focusTarget = target.querySelector('[aria-invalid="true"]') ?? target.querySelector('[role="alert"]');
    if (focusTarget instanceof HTMLElement) {
        if (!focusTarget.matches('input, select, textarea, button')) {
            focusTarget.tabIndex = -1;
        }
        const focus = () => focusTarget.focus({ preventScroll: true });
        focus();
        // The redirect lands on a `#fragment`, and the browser's own fragment navigation runs its focusing
        // steps after this script, putting focus back on <body>; focus again once the page has loaded.
        const documentRef = page.ownerDocument;
        const view = documentRef?.defaultView;
        if (view !== null && view !== undefined && documentRef.readyState !== 'complete') {
            view.addEventListener('load', () => view.setTimeout(focus, 0), { once: true });
        }
    }

    return {
        destroy() {},
    };
}

const EMAIL_DRAFT_KEY = 'game-app-tracker:account:new-email-draft';

/**
 * One form is posted at a time, so a new email typed but not sent would be lost when another form on this
 * page is submitted. It waits in this tab's session storage until its own form is sent - the server keeps
 * the value of a failed change. Passwords are never kept.
 */
function initEmailDraft(page, storage) {
    const formParts = resolveChangeEmailForm(page);
    if (formParts === null || storage === undefined || storage === null) {
        return undefined;
    }

    const { form, emailInput } = formParts;
    const attempt = (operation) => {
        try {
            return operation();
        } catch {
            return null;
        }
    };
    if (emailInput.value === '') {
        emailInput.value = attempt(() => storage.getItem(EMAIL_DRAFT_KEY)) ?? '';
    }
    const remember = () => attempt(() => storage.setItem(EMAIL_DRAFT_KEY, emailInput.value));
    const forget = () => attempt(() => storage.removeItem(EMAIL_DRAFT_KEY));
    emailInput.addEventListener('input', remember);
    form.addEventListener('submit', forget);

    return {
        destroy() {
            emailInput.removeEventListener('input', remember);
            form.removeEventListener('submit', forget);
        },
    };
}

function initExportJump(page, timerApi) {
    const trigger = page.querySelector('[data-account-export-jump]');
    const target = page.querySelector('#account-export-row');

    if (!(trigger instanceof HTMLElement) || !(target instanceof HTMLElement)) {
        return undefined;
    }

    let highlightTimeoutId = null;

    const clearHighlight = () => {
        delete target.dataset.accountExportArrival;
        if (highlightTimeoutId !== null && typeof timerApi.clearTimeout === 'function') {
            timerApi.clearTimeout(highlightTimeoutId);
        }
        highlightTimeoutId = null;
    };

    const handleClick = (event) => {
        if (typeof target.scrollIntoView !== 'function') {
            return;
        }

        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        clearHighlight();
        target.dataset.accountExportArrival = 'active';

        if (typeof timerApi.setTimeout === 'function') {
            highlightTimeoutId = timerApi.setTimeout(() => {
                delete target.dataset.accountExportArrival;
                highlightTimeoutId = null;
            }, 560);
        }
    };

    trigger.addEventListener('click', handleClick);

    return {
        destroy() {
            trigger.removeEventListener('click', handleClick);
            clearHighlight();
        },
    };
}
